import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import KidLogin from './pages/KidLogin';
import ParentLogin from './pages/ParentLogin';
import ParentHome from './pages/ParentHome';
import ParentLearn from './pages/ParentLearn';
import SubjectsHub from './pages/SubjectsHub';
import ParentSocial from './pages/ParentSocial';
import {
  ParentKidSummary, ParentKidHomework, ParentKidTimetable, ParentKidSchedule,
  ParentKidMath, ParentKidReading, ParentKidHistory,
} from './pages/parentKidScreens';
import KidHome from './pages/KidHome';
import KidSocial from './pages/KidSocial';
import DailySummary from './pages/DailySummary';
import Homework from './pages/Homework';
import Timetable from './pages/Timetable';
import Schedule from './pages/Schedule';
import MathQuiz from './pages/MathQuiz';
import Reading from './pages/Reading';
import QuizHistory from './pages/QuizHistory';
import KidActivity from './pages/KidActivity';
import KidMedia from './pages/KidMedia';
import ParentMedia from './pages/ParentMedia';
import Help from './pages/Help';
import KidLayout from './components/KidLayout';
import ParentLayout from './components/ParentLayout';
import useAuthStore from './store/authStore';

// Protected route — redirects to login if not authenticated with the right role
const ProtectedRoute: React.FC<{ children: React.ReactNode; requiredRole: string; redirectTo: string }> = ({
  children, requiredRole, redirectTo
}) => {
  const { token, role } = useAuthStore();
  if (!token || role !== requiredRole) return <Navigate to={redirectTo} replace />;
  return <>{children}</>;
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<KidLogin />} />
        <Route path="/parent/login" element={<ParentLogin />} />

        {/* Protected parent routes — shared sidebar layout, pages render in the right panel */}
        <Route
          element={
            <ProtectedRoute requiredRole="PARENT" redirectTo="/parent/login">
              <ParentLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/parent/home" element={<ParentHome />} />
          <Route path="/parent/learn" element={<ParentLearn />} />
          <Route path="/parent/social" element={<ParentSocial />} />
          {/* Per-kid management hub + the 7 learning screens */}
          <Route path="/parent/kids/:kidId/learn" element={<SubjectsHub mode="parent" />} />
          <Route path="/parent/kids/:kidId/summary" element={<ParentKidSummary />} />
          <Route path="/parent/kids/:kidId/homework" element={<ParentKidHomework />} />
          <Route path="/parent/kids/:kidId/timetable" element={<ParentKidTimetable />} />
          <Route path="/parent/kids/:kidId/schedule" element={<ParentKidSchedule />} />
          <Route path="/parent/kids/:kidId/math" element={<ParentKidMath />} />
          <Route path="/parent/kids/:kidId/reading" element={<ParentKidReading />} />
          <Route path="/parent/kids/:kidId/history" element={<ParentKidHistory />} />
          <Route path="/parent/kids/:kidId/activity" element={<KidActivity />} />
          <Route path="/parent/help" element={<Help mode="parent" />} />
          <Route path="/parent/photos" element={<ParentMedia type="PHOTO" />} />
          <Route path="/parent/videos" element={<ParentMedia type="VIDEO" />} />
          {/* Back-compat redirects to the new structure */}
          <Route path="/parent/dashboard" element={<Navigate to="/parent/home" replace />} />
          <Route path="/parent/friends" element={<Navigate to="/parent/social" replace />} />
          <Route path="/parent/groups" element={<Navigate to="/parent/social" replace />} />
          <Route path="/parent/chat" element={<Navigate to="/parent/social" replace />} />
          <Route path="/parent/media" element={<Navigate to="/parent/photos" replace />} />
        </Route>

        {/* Protected kid routes — shared sidebar layout, pages render in the right panel */}
        <Route
          element={
            <ProtectedRoute requiredRole="KID" redirectTo="/">
              <KidLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/kid/home" element={<KidHome />} />
          <Route path="/kid/learn" element={<SubjectsHub mode="kid" />} />
          <Route path="/kid/social" element={<KidSocial />} />
          <Route path="/kid/summary" element={<DailySummary />} />
          <Route path="/kid/homework" element={<Homework />} />
          <Route path="/kid/timetable" element={<Timetable />} />
          <Route path="/kid/schedule" element={<Schedule />} />
          <Route path="/kid/math" element={<MathQuiz />} />
          <Route path="/kid/reading" element={<Reading />} />
          <Route path="/kid/history" element={<QuizHistory />} />
          <Route path="/kid/help" element={<Help mode="kid" />} />
          <Route path="/kid/photos" element={<KidMedia type="PHOTO" />} />
          <Route path="/kid/videos" element={<KidMedia type="VIDEO" />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
